<script>
import moment from "moment";
import { ChartColorsEnum } from "../constants";

/**
 * @typedef {object} GanttData
 * @property {string} label
 * @property {string} begin
 * @property {string} finish
 */

/**
 * @typedef {object} GanttItem
 * @property {string} label
 * @property {string} begin
 * @property {string} finish
 * @property {string} left
 * @property {string} bgColor
 */

export default {
  props: {
    /** @type {GanttData[]} */
    data: {
      type: Array,
      default: () => ([])
    },
    offsetBack: {
      type: Number,
      default: 2
    },
    offsetForward: {
      type: Number,
      default: 3
    },
    year: {
      type: Number
    }
  },
  data() {
    return {
      ganttChartWidth: 0
    };
  },
  computed: {
    cols() {
      if (!this.year) {
        const { begin, finish } = this.projectDateLimits;
        const months = Array.from({ length: finish - begin + 1 }, (_, index) =>
          (begin + index).toString()
        );

        return ["Título do projeto", ...months];
      }

      const back = Array.from({ length: this.offsetBack }, (_, index) =>
        (this.year - index - 1).toString()
      ).reverse();
      const foward = Array.from({ length: this.offsetForward }, (_, index) =>
        (this.year + index + 1).toString()
      );

      return [
        "Título do projeto",
        ...back,
        "jan",
        "fev",
        "mar",
        "abr",
        "mai",
        "jun",
        "jul",
        "ago",
        "set",
        "out",
        "nov",
        "dez",
        ...foward
      ];
    },
    projectDateLimits() {
      return this.data.reduce(
        ({ begin, finish }, item) => {
          const extractBeginYear = moment(item.begin).get("year");
          const extractFinishYear = moment(item.finish).get("year");

          return {
            begin: Math.min(extractBeginYear, begin),
            finish: Math.max(extractFinishYear, finish)
          };
        },
        { begin: Infinity, finish: -Infinity }
      );
    },
    /**
     * @type {GanttItem[]}
     * @readonly
     */
    rows() {
      const colors = [
        ChartColorsEnum.darkestBlue,
        ChartColorsEnum.darkBlue,
        ChartColorsEnum.darkBlue_25
      ];
      const targetYear = moment().startOf("year");
      let maxYear = targetYear;
      let minYear = targetYear;

      if (this.year) {
        targetYear.set("year", this.year);
        maxYear = moment(targetYear)
          .add(this.offsetForward, "years")
          .endOf("year");
        minYear = moment(targetYear).subtract(this.offsetBack, "years");
      } else {
        const { begin, finish } = this.projectDateLimits;
        maxYear = moment()
          .set("year", finish)
          .endOf("year");
        minYear = moment()
          .set("year", begin)
          .startOf("year");
      }

      let length = maxYear.get("year") - minYear.get("year") + 1;

      if (this.year) {
        length += 12;
      }

      // Removendo a largura da primeira coluna
      const width = this.ganttChartWidth - 300;
      let cellWidth = width / length;

      // Precia ter uma largura mínima de 100px
      if (cellWidth < 100) {
        cellWidth = 100;
      }

      const cellWidthOneTwelfth = Math.floor(cellWidth / 12);

      return this.data.map(({ begin, finish, label }, index) => {
        const beginDate = moment(begin);
        const finishDate = moment(finish);
        let left = 300;
        let right = 0;

        if (this.year) {
          if (
            beginDate.isSameOrAfter(minYear, "year") &&
            beginDate.isBefore(targetYear, "year")
          ) {
            const difference = beginDate.diff(minYear, "months");
            const years = Math.floor(difference / 12);
            const months = difference % 12;
            left += cellWidth * years + cellWidthOneTwelfth * months;
          } else if (beginDate.isSame(targetYear, "year")) {
            const difference = beginDate.diff(minYear, "months");
            const years = Math.floor(difference / 12);
            const months = difference % 12;
            left += cellWidth * (years + months);
          } else if (
            beginDate.isAfter(targetYear, "year") &&
            beginDate.isSameOrBefore(maxYear, "year")
          ) {
            const difference = beginDate.diff(targetYear, "months");
            const years = Math.floor(difference / 12) - 1;
            const months = difference % 12;
            left += cellWidth * (years + 12) + cellWidthOneTwelfth * months;
          } else if (beginDate.isAfter(maxYear, "year")) {
            left = this.ganttChartWidth;
          }

          if (
            finishDate.isSameOrBefore(maxYear, "years") &&
            finishDate.isAfter(targetYear, "years")
          ) {
            const difference = finishDate.diff(maxYear, "months");
            const years = Math.floor(difference / 12);
            const months = difference % 12;
            right += Math.abs(years * cellWidth + months * cellWidthOneTwelfth);
          } else if (finishDate.isSame(targetYear, "years")) {
            const difference = finishDate.diff(maxYear, "months");
            const years = Math.floor(difference / 12);
            const months = difference % 12;
            right += Math.abs(cellWidth * (years + months));
          } else if (
            finishDate.isBefore(targetYear, "year") &&
            finishDate.isSameOrAfter(minYear, "year")
          ) {
            const difference = finishDate.diff(maxYear, "months");
            const years = Math.floor(difference / 12) - 1;
            const months = difference % 12;
            right += cellWidth * (years + 12) + cellWidthOneTwelfth * months;
          } else if (finishDate.isBefore(minYear, "years")) {
            right = this.ganttChartWidth - 300;
          }
        } else {
          if (beginDate.isAfter(maxYear, "years")) {
            left = this.ganttChartWidth;
          } else if (beginDate.isSameOrAfter(minYear, "years")) {
            const difference = beginDate.diff(minYear, "months");
            const years = Math.floor(difference / 12);
            const months = difference % 12;
            left += cellWidth * years + cellWidthOneTwelfth * months;
          }

          if (finishDate.isBefore(minYear, "years")) {
            right = this.ganttChartWidth - 300;
          } else if (finishDate.isSameOrBefore(maxYear, "years")) {
            const difference = finishDate.diff(maxYear, "months");
            const years = Math.floor(difference / 12);
            const months = difference % 12;
            right += Math.abs(cellWidth * years + cellWidthOneTwelfth * months);
          }
        }

        return {
          begin: beginDate.format("DD/MM/YYYY"),
          finish: finishDate.format("DD/MM/YYYY"),
          label,
          left: `${left}px`,
          right: `${right}px`,
          bgColor: colors[index % colors.length],
          cellWidth: `${cellWidth}px`,
          barWidth: `${this.ganttChartWidth - left - right}px`
        };
      });
    }
  },
  watch: {
    "data.length": {
      handler() {
        this.$nextTick(() => {
          this.ganttChartWidth = this.$refs.table?.$el.offsetWidth || 0;
        });
      }
    }
  }
};
</script>

<template>
  <div v-if="!rows.length" class="no-data">
    <p>Nenhum business plan para ser exibido.</p>
  </div>

  <v-card v-else class="gantt-chart" ref="table">
    <div class="gantt-headers">
      <div
        v-for="(col, index) of cols"
        :key="index"
        class="gantt-header"
        :style="{ minWidth: index !== 0 && rows[0].cellWidth }"
      >
        {{ col }}
      </div>
    </div>

    <div class="gantt-rows">
      <div v-for="(row, rowI) of rows" :key="rowI" class="gantt-row">
        <div
          v-for="(col, colI) of cols"
          :key="colI"
          class="gantt-cell"
          :style="{ width: colI !== 0 && row.cellWidth }"
        >
          <span v-if="colI === 0">
            <v-tooltip top nudge-right="10">
              <template v-slot:activator="{ on }">
                <span v-on="on">{{ row.label }}</span>
              </template>

              <span>{{ row.label }}</span>
            </v-tooltip>
          </span>
        </div>
        <div
          class="gantt-horizontal-bar"
          :style="{
            backgroundColor: row.bgColor,
            left: row.left,
            right: row.right
          }"
        >
          <v-tooltip top nudge-right="10">
            <template v-slot:activator="{ on }">
              <span v-on="on" :style="{ width: row.barWidth }"
                >{{ row.begin }} - {{ row.finish }}</span
              >
            </template>

            <span>{{ row.begin }} - {{ row.finish }}</span>
          </v-tooltip>
        </div>
      </div>
    </div>
  </v-card>
</template>

<style scoped lang="scss">
.no-data {
  background-color: #215f9a;
  color: #fff;
  display: flex;
  font-size: 1.05rem;
  font-weight: 500;
  justify-content: center;
  padding: 0.75rem;
  width: 100%;

  p {
    margin: 0;
  }
}

.gantt-chart {
  background-color: #215f9a;
  overflow-y: auto;
  width: 100%;

  .gantt-headers {
    display: flex;

    .gantt-header {
      background-color: #215f9a;
      color: #fff;
      text-align: center;
      min-width: 100px;

      &:nth-child(1) {
        text-align: start;
        min-width: 300px;
        width: 300px;
      }
    }
  }

  .gantt-rows {
    background-color: #fff;
    width: fit-content;
  }

  .gantt-row {
    display: flex;
    position: relative;
  }

  .gantt-cell {
    border-bottom: thin solid #c3cfe1;
    border-left: thin solid #c3cfe1;
    min-width: 100px;

    &:nth-child(1) {
      text-align: start;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 300px;
      min-width: 300px;
      width: 300px !important;
    }
  }

  .gantt-cell,
  .gantt-header {
    padding: 0.875rem 0.5rem;
    height: 3rem;
  }

  .gantt-horizontal-bar {
    color: #fff;
    border-radius: 10px;
    font-weight: 500;
    padding: 0.5rem 0;
    position: absolute;
    height: 2rem;
    text-align: center;
    top: 0.5rem;
    z-index: 10;

    span {
      text-align: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: inline-block;
    }
  }
}
</style>
